import React, { useState, useEffect } from 'react';

// Material-UI
import { styled } from '@mui/material/styles';
import {
    Box,
    TextField,
    Button,
    Grid,
    Typography,
    CircularProgress,
    FormControlLabel,
    FormHelperText,
    Alert,
} from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import { Table, TableBody, TableRow, TableCell as _TableCell } from '@mui/material';
import { Radio, RadioGroup } from '@mui/material';

// Components
import { Page } from 'components/ui/layout';
import EventPayoutsTable from 'components/payouts/EventPayoutsTable';
import Incidentals from 'components/admin/incidentals/Incidentals';
import { DatePicker, TimePicker } from 'components/ui/inputs';
import { CancelButton } from 'components/ui/buttons';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { useNotification } from 'context/notification';
import { getMomentFromDateAndTime } from 'utility/validation';
import { formatPrice } from 'utility/numbers';
import { useDatePicker, useNumberInput, useTextInput } from 'hooks/forms';

export default function PayoutDialog(props) {
    const { eventId, eventName, hostName, startDate, onSuccess, onCancel } = props;

    // State
    const [state, setState] = useState({
        status: 'loading',
        submitting: false,
        totals: null,
        connect_type_id: null,
        payouts_enabled: null,
        connect_balance: null,
        payouts: null,
        incidentals: null,
    });

    const [refresh, setRefresh] = useState(0);

    // Form Values
    const amount = useNumberInput({ format: 'currency' });
    const date = useDatePicker({ defaultValue: moment() });
    const time = useDatePicker({ defaultValue: moment() });
    const payoutType = useTextInput(); // final | partial (default "")

    const [payoutAllowed, setPayoutAllowed] = useState(true);

    const { notify } = useNotification();

    useEffect(() => {
        Promise.all([
            fetchExpresso(`/apiv2/events/${eventId}/payouts?tz=${moment.tz.guess()}`),
            fetchExpresso(`/apiv2/events/${eventId}/payout_stats?tz=${moment.tz.guess()}`),
        ])
            .then((res) => {
                if (res[0].status !== 200 || res[1].status !== 200) {
                    throw new Error();
                }
                return Promise.all([res[0].json(), res[1].json()]);
            })
            .then((data) => {
                const [payouts, stats] = data;

                // Event has been given its final payout
                if (payouts.some((p) => p.final_payout_flag === 1)) {
                    setPayoutAllowed(false);
                }

                const scheduledPayouts = stats.scheduled_payout_flag === 1;

                const totals = {
                    payout_reserve_percent: stats.payout_reserve_percent || 0,
                    total_revenue: stats.total_revenue || 0,
                    total_incidentals: stats.total_incidentals || 0,
                    total_payouts: stats.total_payouts || 0,
                };

                const incidentals = stats.incidentals || [];

                setState((s) => ({
                    ...s,
                    status: 'success',
                    scheduledPayouts: scheduledPayouts,
                    totals: totals,
                    connect_type_id: stats.connect_type_id,
                    payouts_enabled: stats.payouts_enabled,
                    connect_balance: stats.connect_balance,
                    payouts: payouts,
                    incidentals: incidentals,
                }));
            })
            .catch(() => {
                setState((s) => ({ ...s, status: 'error' }));
            });
    }, [eventId, refresh]);

    const handleSubmit = () => {
        if (state.submitting) return;

        let formError = false;

        if (!amount.value) {
            amount.setError('Please enter an amount');
            formError = true;
        }

        if (!date.value.isValid()) {
            date.setError('Please enter a valid date');
            formError = true;
        }

        if (!time.value.isValid()) {
            time.setError('Please enter a valid time');
            formError = true;
        }

        if (payoutType.value !== 'final' && payoutType.value !== 'partial') {
            payoutType.setError('Please select an option');
            formError = true;
        }

        if (formError) return;

        setState((s) => ({ ...s, submitting: true }));

        fetchExpresso(`/admin/events/${eventId}/payouts`, {
            method: 'POST',
            body: {
                amount: Number(amount.value),
                date: getMomentFromDateAndTime(date.value, time.value).format('YYYY-MM-DD HH:mm:ss'),
                final: payoutType.value === 'final',
            },
        }).then((res) => {
            if (res.status === 200) {
                notify.success('Payout successful');
                onSuccess();
            } else {
                notify.error('Unable to payout host');
                setState((s) => ({ ...s, submitting: false }));
            }
        });
    };

    const copyToClipboard = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            notify.info('Copied to clipboard!');
        } catch (e) {
            notify.error('Unable to copy to clipboard');
        }
    };

    if (state.status === 'loading') {
        return (
            <Page>
                <Box display="flex" justifyContent="center" alignItems="center" height={72}>
                    <CircularProgress />
                </Box>
            </Page>
        );
    }

    if (state.status === 'error') {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <Typography variant="h5">There was a problem finding your payouts</Typography>
                </Box>
            </Page>
        );
    }

    const payout_reserve_percent = state.totals?.payout_reserve_percent || 0;
    const total_revenue = state.totals?.total_revenue || 0;
    const total_incidentals = state.totals?.total_incidentals || 0;
    const total_payouts = state.totals?.total_payouts || 0;

    const totalPayout = Number((total_revenue - total_incidentals).toFixed(2));

    let reserve =
        payoutType.value === 'final'
            ? 0
            : Number((totalPayout * payout_reserve_percent).toFixed(2));

    const nextPayout = Number(
        (Number((totalPayout - total_payouts).toFixed(2)) - reserve).toFixed(2),
    );

    const incUnpaidCount = state.incidentals?.reduce(
        (count, inc) => (inc.inc_status_id !== 90 ? count + 1 : count),
        0,
    );

    return (
        <Root>
            <Container>
                <Page width="lg">
                    <Box mb={4}>
                        <ConnectAlert
                            connect_type_id={state.connect_type_id}
                            payouts_enabled={state.payouts_enabled}
                        />
                        <UnpaidIncidentalAlert unpaidCount={incUnpaidCount} />
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardHeader title="Event" />
                                <CardContent>
                                    <MetaData>{eventName}</MetaData>
                                    <MetaData>{hostName}</MetaData>
                                    <MetaData>
                                        {moment(startDate).format('YYYY-MM-DD h:mm a')}
                                    </MetaData>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardHeader title="Breakdown" />
                                <CardContent>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Total Revenue</TableCell>
                                                <TableCell align="right">
                                                    {formatPrice(total_revenue)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Total Incidentals</TableCell>
                                                <TableCell align="right">
                                                    - {formatPrice(total_incidentals)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <SubTotalBlack>Total Payout</SubTotalBlack>
                                                <SubTotalBlack align="right">
                                                    {formatPrice(totalPayout)}
                                                </SubTotalBlack>
                                            </TableRow>
                                            <TableRow>
                                                <TableCellTopPadding>
                                                    Reserve (
                                                    {Number(
                                                        (payout_reserve_percent * 100).toFixed(2),
                                                    ) + '%'}
                                                    )
                                                </TableCellTopPadding>
                                                <TableCellTopPadding align="right">
                                                    - {formatPrice(reserve)}
                                                </TableCellTopPadding>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Total Paid Out</TableCell>
                                                <TableCell align="right">
                                                    - {formatPrice(total_payouts)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <SubtotalCell>Next Payout</SubtotalCell>
                                                <SubtotalCell align="right">
                                                    {formatPrice(nextPayout)}
                                                </SubtotalCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Make a Payment" />
                                <CardContent>
                                    <Box mb={2}>
                                        <RadioGroup aria-label="audience" {...payoutType.formProps}>
                                            <FormControlLabel
                                                value="final"
                                                control={<Radio />}
                                                label="Final Payout"
                                                disabled={payoutAllowed === false}
                                            />
                                            <FormControlLabel
                                                value="partial"
                                                control={<Radio />}
                                                label="Partial Payout"
                                                disabled={payoutAllowed === false}
                                            />
                                            {payoutType.formProps.error && (
                                                <FormHelperText error>
                                                    {payoutType.formProps.helperText}
                                                </FormHelperText>
                                            )}
                                        </RadioGroup>
                                        <AmountDueText onClick={() => copyToClipboard(nextPayout)}>
                                            Amount Due: {formatPrice(nextPayout)}
                                        </AmountDueText>
                                        {state.connect_type_id === 1 ||
                                            state.connect_type_id === 2 ? (
                                            <AmountDueText>
                                                Connect Balance:{' '}
                                                {formatPrice(state.connect_balance)}
                                            </AmountDueText>
                                        ) : (
                                            <AmountDueText>Connect Balance: n/a</AmountDueText>
                                        )}
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                {...amount.formProps}
                                                disabled={payoutAllowed === false}
                                                autoFocus
                                                fullWidth
                                                variant="outlined"
                                                label="$ Amount"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DatePicker
                                                {...date.formProps}
                                                disabled={payoutAllowed === false}
                                                label="Date"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TimePicker
                                                {...time.formProps}
                                                disabled={payoutAllowed === false}
                                                label="Time"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <CardActions>
                                    <CancelButton
                                        disabled={state.submitting}
                                        onClick={onCancel}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </CancelButton>
                                    <Button
                                        disabled={state.submitting || payoutAllowed === false}
                                        onClick={handleSubmit}
                                        variant="contained"
                                        color="secondary"
                                    >
                                        Pay
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Payout History" />
                                <CardContent>
                                    <EventPayoutsTable payouts={state.payouts} />
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Incidentals
                                eventId={eventId}
                                onUpdate={() => setRefresh((r) => r + 1)}
                            />
                        </Grid>
                    </Grid>
                </Page>
            </Container>
        </Root>
    );
}

function ConnectAlert({ connect_type_id, payouts_enabled }) {
    // Stripe Connect Payout
    if (connect_type_id === 1 || connect_type_id === 2) {
        if (payouts_enabled) {
            return <Alert severity="success">Stripe Connect payouts are supported.</Alert>;
        } else {
            return (
                <Alert severity="error">
                    Payouts for this Stripe Connect account are disabled.
                </Alert>
            );
        }
    } else if (connect_type_id === 3) {
        return (
            <Alert severity="info">
                This host uses a Standard Stripe account. No payout required
            </Alert>
        );
    }

    // Manual Payout Required
    else {
        return (
            <Alert severity="info">
                This host does not use Stripe Connect. Manual payouts are required.
            </Alert>
        );
    }
}

function UnpaidIncidentalAlert({ unpaidCount }) {
    if (unpaidCount > 0) {
        return (
            <Box mt={1}>
                <Alert severity="warning">
                    {unpaidCount}{' '}
                    {unpaidCount === 1 ? 'incidental has not' : 'incidentals have not'} been
                    collected yet
                </Alert>
            </Box>
        );
    }
    return null;
}

const Root = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    height: '100%',
}));

const Container = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
}));

const TableCell = styled(_TableCell)(({ theme }) => ({
    border: 'none',
    paddingTop: 0,
}));

const TableCellTopPadding = styled(_TableCell)(({ theme }) => ({
    border: 'none',
}));

const SubtotalCell = styled(_TableCell)(({ theme }) => ({
    border: 'none',
    borderTop: '0.5px solid',
    fontWeight: 'bold',
}));

const SubTotalBlack = styled(_TableCell)(({ theme }) => ({
    border: 'none',
    paddingTop: 0,
    borderBottom: '0.5px solid',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
}));

const MetaData = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const AmountDueText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
}));
